import { compareDesc } from 'date-fns';
import numeral from 'numeral';

import {
  ComparisonPeriod,
  DisplayType,
  IndicatorType,
  KpiEmbed,
  KpiValue,
  KpiWithDisplay,
  Trend,
} from 'domain/kpis/types';
import sort from 'ramda/src/sort';
import { TinyCompanyDTO } from 'types/company';

export const formatAsKpi = (value: number) => numeral(value).format('0,0.[00]');

export const sortKpiValuesDesc = (values: KpiValue[]) =>
  sort((a, b) => compareDesc(new Date(a.timestamp), new Date(b.timestamp)), values);

const numberOrParseString = (n: number | string) => {
  if (typeof n === 'number') {
    return n;
  }
  return parseFloat(n);
};

export function getChangeInPercent(values: KpiValue[]): number {
  const len = values.length;
  if (len < 2) {
    return 0;
  }

  const sortedValues = sortKpiValuesDesc(values);

  const newestValue = numberOrParseString(sortedValues[0].value);
  const secondNewestValue = numberOrParseString(sortedValues[1].value);
  return parseFloat(formatAsKpi(((newestValue - secondNewestValue) / secondNewestValue) * 100));
}

export const getTrend = (trendIndicator: IndicatorType, values: KpiValue[]): Trend => {
  const twoLatestValues = sortKpiValuesDesc(values)
    .slice(0, 2)
    .map(v => v.value);

  if (twoLatestValues.length < 2 || twoLatestValues[1] === twoLatestValues[0]) return 'neutral';

  if (trendIndicator === 'Up') return twoLatestValues[0] > twoLatestValues[1] ? 'positive' : 'negative';

  return twoLatestValues[1] > twoLatestValues[0] ? 'positive' : 'negative';
};

/**
 * Gets the comparison value from exactly N periods ago based on comparisonPeriod
 * Only returns a value if data exists for that specific period
 */
export function getComparisonValue(
  values: KpiValue[],
  comparisonPeriod: ComparisonPeriod,
  currentTimestamp: string,
): { value: number; timestamp: string } | null {
  if (values.length < 2) return null;

  const currentDate = new Date(currentTimestamp);
  let targetDate: Date;

  if (comparisonPeriod === 'Month') {
    // Go back exactly 1 month
    targetDate = new Date(currentDate);
    targetDate.setMonth(currentDate.getMonth() - 1);
  } else {
    // Go back exactly 1 year
    targetDate = new Date(currentDate);
    targetDate.setFullYear(currentDate.getFullYear() - 1);
  }

  // Only accept values within 5 days of the target date (to account for end-of-month variations)
  const ACCEPTABLE_DIFF_DAYS = 5;
  const acceptableDiffMs = ACCEPTABLE_DIFF_DAYS * 24 * 60 * 60 * 1000;

  // Find a value that matches the target period closely
  for (const value of values) {
    const valueDate = new Date(value.timestamp);
    const diff = Math.abs(valueDate.getTime() - targetDate.getTime());

    if (diff <= acceptableDiffMs) {
      return {
        value: numberOrParseString(value.value),
        timestamp: value.timestamp,
      };
    }
  }

  return null;
}

/**
 * Calculates percentage change between current and comparison value
 */
export function calculateComparison(currentValue: number, comparisonValue: number): number {
  if (comparisonValue === 0) return 0;
  return Math.round(((currentValue - comparisonValue) / comparisonValue) * 100);
}

/**
 * Gets only the specific values needed for display (not intermediate ones)
 * For NumberIndicator with comparison, returns only current + comparison value
 * For BarChart with comparison, returns double the data points (current + previous year)
 * For other charts, returns the last N data points
 */
export function getValuesToEmbed(
  values: KpiValue[],
  displayType: DisplayType,
  comparisonEnabled: boolean,
  comparisonPeriod: ComparisonPeriod,
  dataPointCount: number,
): KpiValue[] {
  if (displayType === 'NumberIndicator' && comparisonEnabled && values.length >= 2) {
    // For NumberIndicator with comparison, only include the current and comparison values
    const sortedValues = sortKpiValuesDesc(values);
    const currentValue = sortedValues[0];
    const comparisonData = getComparisonValue(values, comparisonPeriod, currentValue.timestamp);

    if (comparisonData) {
      // Return in chronological order (comparison first, then current)
      return [{ timestamp: comparisonData.timestamp, value: comparisonData.value }, currentValue];
    }
    // If no comparison found, just return current value
    return [currentValue];
  }

  if (displayType === 'BarChart' && comparisonEnabled) {
    // For BarChart with YoY comparison, we need:
    // - Last N months (current data points to show)
    // - Plus 12 months before that (to have comparison data from previous year)
    // Total: N + 12 months of data
    return values.slice(-(dataPointCount + 12));
  }

  // For other cases (charts without comparison, or NumberIndicator without comparison), return last N values
  return values.slice(-dataPointCount);
}

export function kpiAsEmbed(kpi: KpiWithDisplay, company: TinyCompanyDTO) {
  const isNumberIndicator = kpi.display.displayType === 'NumberIndicator';
  const isBarChart = kpi.display.displayType === 'BarChart';

  // Use maxDataPoints for bar charts if available, otherwise use defaults
  let defaultDataPoints: number;
  if (isNumberIndicator) {
    defaultDataPoints = kpi.display.comparisonEnabled ? 2 : 1; // 1-2 for NumberIndicator based on comparison
  } else if (isBarChart && kpi.display.maxDataPoints) {
    defaultDataPoints = kpi.display.maxDataPoints; // Use configured maxDataPoints for BarChart
  } else {
    defaultDataPoints = 12; // 12 for LineChart or BarChart without maxDataPoints
  }

  const now = new Date().toISOString();

  // Bar charts always compare with previous year
  const comparisonPeriod = isBarChart ? 'Year' : (kpi.display.comparisonPeriod ?? 'Month');

  // Get only the specific values needed for display
  const embeddedValues = getValuesToEmbed(
    kpi.values,
    kpi.display.displayType,
    kpi.display.comparisonEnabled ?? false,
    comparisonPeriod,
    defaultDataPoints,
  );

  const embed: KpiEmbed = {
    id: `embed-${Date.now()}`,
    kpiId: kpi.details.id,
    displayConfig: {
      displayType: kpi.display.displayType,
      dataPointCount: defaultDataPoints,
      dateRange: {
        start: kpi.values[0]?.timestamp || now,
        end: kpi.values[kpi.values.length - 1]?.timestamp || now,
      },
      showXAxis: kpi.display.showXAxis,
      showYAxis: kpi.display.showYAxis,
      comparisonEnabled: kpi.display.comparisonEnabled ?? false,
      comparisonPeriod: comparisonPeriod,
      trendIndicator: kpi.display.trendIndicator ?? 'Up',
    },
    metadata: {
      kpiName: kpi.details.name,
      unitOfMeasure: kpi.details.unitOfMeasure || '',
      companyId: company.id,
      companySlug: company.slug,
    },
    values: embeddedValues,
  };
  return embed;
}
